import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FaKey,
  FaLock,
  FaCopy,
  FaEye,
  FaEyeSlash,
  FaBell,
  FaBuilding,
  FaPlug,
  FaCheck,
  FaTimes,
  FaExternalLinkAlt,
  FaTrash,
  FaEdit
} from 'react-icons/fa';
import axios from '../../../lib/axios';
import { Modal, Button } from '../../../UI';
import Container from './styles';

const Settings = () => {
  const { userData } = useSelector((s) => s.UserData);
  const navigate = useNavigate();

  // Tab State
  const [activeTab, setActiveTab] = useState('general');

  // Password Change State
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

  // API Key State
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [showApiKey, setShowApiKey] = useState({});
  const [newKeyName, setNewKeyName] = useState('');
  const [deleteModal, setDeleteModal] = useState({ show: false, keyId: null, keyName: '' });

  // Company Settings State
  const [companySettings, setCompanySettings] = useState({
    companyName: '',
    email: '',
    phone: '',
    address: '',
    notificationsEnabled: true,
    emailNotifications: true,
    smsNotifications: false
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState({ type: '', text: '' });

  // Webhooks State
  const [webhooks, setWebhooks] = useState([]);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [webhookFormData, setWebhookFormData] = useState({
    name: '',
    url: '',
    secret: '',
    events: []
  });
  const [showWebhookForm, setShowWebhookForm] = useState(false);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookLogs, setWebhookLogs] = useState([]);

  // Populate form with userData on mount
  useEffect(() => {
    if (userData) {
      setCompanySettings(prev => ({
        ...prev,
        companyName: userData.companyName || '',
        email: userData.email || '',
        phone: userData.phone || userData.contactPhone || '',
        address: userData.address || userData.businessAddress || ''
      }));
    }
  }, [userData]);

  useEffect(() => {
    console.log('Component mounted, initial fetch');
    fetchApiKeys();
    fetchCompanySettings();
    fetchWebhooks(); // Fetch webhooks on mount too
  }, []);

  useEffect(() => {
    console.log('Active tab changed to:', activeTab);
    if (activeTab === 'webhooks') {
      console.log('Webhooks tab active, fetching webhooks...');
      fetchWebhooks();
    }
  }, [activeTab]);

  // Debug: Log apiKeys when they change
  useEffect(() => {
    console.log('API Keys state updated:', apiKeys);
  }, [apiKeys]);

  // Debug: Log webhooks when they change
  useEffect(() => {
    console.log('Webhooks state updated:', webhooks);
    console.log('Webhooks is array?', Array.isArray(webhooks));
    console.log('Webhooks length:', webhooks?.length);
  }, [webhooks]);

  // Fetch API Keys
  const fetchApiKeys = async () => {
    try {
      const response = await axios.get('/company/api-keys');
      if (response.data) {
        setApiKeys(response.data);
      }
    } catch (error) {
      console.error('Error fetching API keys:', error);
    }
  };

  // Fetch Company Settings
  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get('/company/settings');
      if (response.data.data) {
        setCompanySettings(prev => ({
          ...prev,
          ...response.data.data
        }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      // If API fails, keep using userData values already set
    }
  };

  // Handle Password Change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordMessage({ type: '', text: '' });

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }

    setPasswordLoading(true);

    try {
      await axios.post('/company/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      setPasswordMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to change password'
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  // Generate New API Key
  const handleGenerateApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }

    setApiKeyLoading(true);

    try {
      const response = await axios.post('/company/api-keys', {
        name: newKeyName
      });

      setApiKeys([...apiKeys, response.data.data]);
      setNewKeyName('');
      toast.success('API Key generated successfully! Make sure to copy it now.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate API key');
    } finally {
      setApiKeyLoading(false);
    }
  };

  // Delete API Key
  const handleDeleteApiKey = async (keyId) => {
    try {
      await axios.delete(`/company/api-keys/${keyId}`);
      setApiKeys(apiKeys.filter(key => key.id !== keyId));
      toast.success('API key deleted successfully');
      setDeleteModal({ show: false, keyId: null, keyName: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete API key');
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (keyId, keyName) => {
    setDeleteModal({ show: true, keyId, keyName });
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setDeleteModal({ show: false, keyId: null, keyName: '' });
  };

  // Copy to Clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  // Update Company Settings
  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    setSettingsMessage({ type: '', text: '' });
    setSettingsLoading(true);

    try {
      await axios.put('/company/settings', companySettings);
      setSettingsMessage({ type: 'success', text: 'Settings updated successfully!' });
    } catch (error) {
      setSettingsMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update settings'
      });
    } finally {
      setSettingsLoading(false);
    }
  };

  // Fetch Webhooks
  const fetchWebhooks = async () => {
    try {
      console.log('Fetching webhooks from /company/webhooks...');
      const response = await axios.get('/company/webhooks');
      console.log('Webhook API response:', response.data);
      
      if (response.data) {
        const webhooksList = response.data;
        console.log('Raw webhooks list:', webhooksList);
        console.log('Is array?', Array.isArray(webhooksList));
        console.log('Length:', webhooksList.length);
        
        // Set webhooks directly without filtering since backend returns valid data
        setWebhooks(webhooksList);
        console.log('Webhooks state updated with', webhooksList.length, 'items');
      } else {
        console.log('No data in response, setting empty array');
        setWebhooks([]);
      }
    } catch (error) {
      console.error('Error fetching webhooks:', error);
      console.error('Error response:', error.response?.data);
      setWebhooks([]);
    }
  };

  // Fetch Webhook Logs
  const fetchWebhookLogs = async (webhookId) => {
    try {
      const response = await axios.get(`/company/webhooks/${webhookId}/logs`);
      if (response.data?.data) {
        setWebhookLogs(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching webhook logs:', error);
    }
  };

  // Available webhook events
  const availableEvents = [
    { id: 'order.created', label: 'Order Created', description: 'Triggered when a new order is created' },
    { id: 'order.updated', label: 'Order Updated', description: 'Triggered when an order is updated' },
    { id: 'order.status_changed', label: 'Order Status Changed', description: 'Triggered when order status changes' },
    { id: 'shipment.picked_up', label: 'Shipment Picked Up', description: 'Triggered when rider picks up shipment' },
    { id: 'shipment.delivered', label: 'Shipment Delivered', description: 'Triggered when shipment is delivered' },
    { id: 'shipment.failed', label: 'Shipment Failed', description: 'Triggered when delivery fails' }
  ];

  // Handle webhook form submit
  const handleWebhookSubmit = async (e) => {
    e.preventDefault();
    
    if (!webhookFormData.name || !webhookFormData.url) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (webhookFormData.events.length === 0) {
      toast.error('Please select at least one event');
      return;
    }

    setWebhookLoading(true);

    try {
      if (selectedWebhook) {
        // Update existing webhook
        const response = await axios.put(`/company/webhooks/${selectedWebhook.id}`, webhookFormData);
        const updatedWebhook = response.data.data;
        setWebhooks(webhooks.map(w => w.id === selectedWebhook.id ? updatedWebhook : w));
        setSelectedWebhook(updatedWebhook); // Update selectedWebhook with fresh data
        toast.success('Webhook updated successfully');
      } else {
        // Create new webhook
        const response = await axios.post('/company/webhooks', webhookFormData);
        const newWebhook = response.data.data;
        setWebhooks([...webhooks, newWebhook]);
        setSelectedWebhook(newWebhook); // Set the newly created webhook as selected
        toast.success('Webhook created successfully');
      }
      
      // Reset form and show details view
      setShowWebhookForm(false);
      setWebhookFormData({ name: '', url: '', secret: '', events: [] });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save webhook');
    } finally {
      setWebhookLoading(false);
    }
  };

  // Handle webhook delete
  const handleDeleteWebhook = async (webhookId) => {
    if (!window.confirm('Are you sure you want to delete this webhook?')) {
      return;
    }

    try {
      await axios.delete(`/company/webhooks/${webhookId}`);
      setWebhooks(webhooks.filter(w => w.id !== webhookId));
      toast.success('Webhook deleted successfully');
      if (selectedWebhook?.id === webhookId) {
        setSelectedWebhook(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete webhook');
    }
  };

  // Handle edit webhook
  const handleEditWebhook = (webhook) => {
    setSelectedWebhook(webhook);
    setWebhookFormData({
      name: webhook.name,
      url: webhook.url,
      secret: webhook.secret || '',
      events: webhook.events || []
    });
    setShowWebhookForm(true);
  };

  // Handle test webhook
  const handleTestWebhook = async (webhookId) => {
    try {
      await axios.post(`/company/webhooks/${webhookId}/test`);
      toast.success('Test event sent to webhook');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to test webhook');
    }
  };

  // Toggle event subscription
  const toggleEvent = (eventId) => {
    setWebhookFormData(prev => ({
      ...prev,
      events: prev.events.includes(eventId)
        ? prev.events.filter(e => e !== eventId)
        : [...prev.events, eventId]
    }));
  };

  // View webhook details
  const viewWebhookDetails = (webhook) => {
    if (!webhook || !webhook.id) {
      console.error('Invalid webhook passed to viewWebhookDetails:', webhook);
      return;
    }
    setSelectedWebhook(webhook);
    setShowWebhookForm(false);
    fetchWebhookLogs(webhook.id);
  };

  return (
    <Container>
      <div className="settings--header">
        <h1>Settings</h1>
        <p>Manage your account settings and preferences</p>
      </div>

      {/* Tabs Navigation */}
      <div className="tabs--navigation">
        <button
          className={`tab--button ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          <FaBuilding />
          General
        </button>
        <button
          className={`tab--button ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          <FaLock />
          Security
        </button>
        <button
          className={`tab--button ${activeTab === 'api' ? 'active' : ''}`}
          onClick={() => setActiveTab('api')}
        >
          <FaKey />
          Documentation & API Keys
        </button>
        <button
          className={`tab--button ${activeTab === 'webhooks' ? 'active' : ''}`}
          onClick={() => setActiveTab('webhooks')}
        >
          <FaPlug />
          Webhooks
        </button>
      </div>

      <div className="settings--content">
        {/* General Tab */}
        {activeTab === 'general' && (
          <>
            {/* Company Information Section */}
            <section className="settings--section">
              <div className="section--header">
                <FaBuilding className="icon" />
                <h2>Company Information</h2>
              </div>

              <form onSubmit={handleUpdateSettings} className="settings--form">
                <div className="form--grid">
                  <div className="form--group">
                    <label>Company Name</label>
                    <input
                      type="text"
                      value={companySettings.companyName}
                      onChange={(e) => setCompanySettings({ ...companySettings, companyName: e.target.value })}
                      placeholder="Enter company name"
                    />
                  </div>

                  <div className="form--group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={companySettings.email}
                      onChange={(e) => setCompanySettings({ ...companySettings, email: e.target.value })}
                      placeholder="company@example.com"
                    />
                  </div>

                  <div className="form--group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={companySettings.phone}
                      onChange={(e) => setCompanySettings({ ...companySettings, phone: e.target.value })}
                      placeholder="+234 XXX XXX XXXX"
                    />
                  </div>

                  <div className="form--group full-width">
                    <label>Address</label>
                    <textarea
                      value={companySettings.address}
                      onChange={(e) => setCompanySettings({ ...companySettings, address: e.target.value })}
                      placeholder="Enter company address"
                      rows="3"
                    />
                  </div>
                </div>

                {settingsMessage.text && (
                  <div className={`message ${settingsMessage.type}`}>
                    {settingsMessage.text}
                  </div>
                )}

                <button type="submit" className="btn btn--primary" disabled={settingsLoading}>
                  {settingsLoading ? 'Updating...' : 'Update Information'}
                </button>
              </form>
            </section>

            {/* Notification Settings */}
            <section className="settings--section">
              <div className="section--header">
                <FaBell className="icon" />
                <h2>Notification Preferences</h2>
              </div>

              <div className="notification--options">
                <div className="notification--item">
                  <div className="notification--info">
                    <strong>Email Notifications</strong>
                    <p>Receive order updates via email</p>
                  </div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={companySettings.emailNotifications}
                      onChange={(e) => setCompanySettings({ ...companySettings, emailNotifications: e.target.checked })}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                <div className="notification--item">
                  <div className="notification--info">
                    <strong>SMS Notifications</strong>
                    <p>Receive order updates via SMS</p>
                  </div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={companySettings.smsNotifications}
                      onChange={(e) => setCompanySettings({ ...companySettings, smsNotifications: e.target.checked })}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>
            </section>
          </>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <>
            {/* Change Password Section */}
            <section className="settings--section">
              <div className="section--header">
                <FaLock className="icon" />
                <h2>Change Password</h2>
              </div>

              <form onSubmit={handlePasswordChange} className="settings--form">
                <div className="form--group">
                  <label>Current Password</label>
                  <div className="password--input">
                    <input
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      placeholder="Enter current password"
                      required
                    />
                    <button
                      type="button"
                      className="toggle--visibility"
                      onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                    >
                      {showPasswords.current ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <div className="form--group">
                  <label>New Password</label>
                  <div className="password--input">
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      placeholder="Enter new password"
                      required
                    />
                    <button
                      type="button"
                      className="toggle--visibility"
                      onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                    >
                      {showPasswords.new ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <div className="form--group">
                  <label>Confirm New Password</label>
                  <div className="password--input">
                    <input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      placeholder="Confirm new password"
                      required
                    />
                    <button
                      type="button"
                      className="toggle--visibility"
                      onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                    >
                      {showPasswords.confirm ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                {passwordMessage.text && (
                  <div className={`message ${passwordMessage.type}`}>
                    {passwordMessage.text}
                  </div>
                )}

                <button type="submit" className="btn btn--primary" disabled={passwordLoading}>
                  {passwordLoading ? 'Changing Password...' : 'Change Password'}
                </button>
              </form>
            </section>
          </>
        )}

        {/* Documentation & API Keys Tab */}
        {activeTab === 'api' && (
          <>
            {/* API Documentation Section */}
            <section className="settings--section">
              <div className="section--header">
                <FaKey className="icon" />
                <h2>API Documentation</h2>
              </div>

              <p className="section--description">
                Integrate Astar Logistics into your application using our REST API. Create orders, track deliveries, and manage logistics programmatically.
              </p>

              <div className="documentation--card">
                <div className="doc--card--icon">
                  📚
                </div>
                <div className="doc--card--content">
                  <h3>Complete API Documentation</h3>
                  <p>
                    Learn how to use our REST API to create delivery orders, track shipments in real-time,
                    and integrate our logistics services into your platform. Includes code examples in JavaScript, PHP, and Python.
                  </p>
                  <ul className="doc--features">
                    <li>✅ Create delivery orders</li>
                    <li>✅ Track order status</li>
                    <li>✅ Retrieve order history</li>
                    <li>✅ Code examples & SDKs</li>
                  </ul>
                </div>
                <div className="doc--card--actions">
                  <button
                    onClick={() => navigate('/company/dashboard/api-docs')}
                    className="btn btn--primary"
                  >
                    View Documentation
                  </button>
                  <button
                    onClick={() => navigate('/company/dashboard/api-docs')}
                    className="btn btn--secondary"
                  >
                    Code Examples
                  </button>
                </div>
              </div>
            </section>

            {/* API Keys Section */}
            <section className="settings--section">
              <div className="section--header">
                <FaKey className="icon" />
                <h2>API Keys</h2>
              </div>

              <p className="section--description">
                Use API keys to integrate Astar Logistics with your applications.
                Keep your keys secure and never share them publicly.
              </p>

              <div className="api--key--generator">
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="Enter API key name (e.g., 'Production', 'Testing')"
                  className="api--key--input"
                />
                <button
                  onClick={handleGenerateApiKey}
                  className="btn btn--generate"
                  disabled={apiKeyLoading}
                >
                  <FaKey className="icon" />
                  {apiKeyLoading ? 'Generating...' : 'Generate New Key'}
                </button>
              </div>

              <div className="api--keys--list">
                {apiKeys.length === 0 ? (
                  <div className="empty--state">
                    <FaKey className="empty--icon" />
                    <p>No API keys yet. Generate one to get started.</p>
                  </div>
                ) : (
                  apiKeys.filter(key => key && key.id).map((key) => (
                    <div key={key.id} className="api--key--item">
                      <div className="api--key--info">
                        <div className="api--key--header">
                          <strong>{key.name || 'Unnamed Key'}</strong>
                          <span className="api--key--status active">Active</span>
                        </div>
                        <div className="api--key--value">
                          <code>
                            {showApiKey[key.id] ? key.key : '••••••••••••••••••••••••••••••••'}
                          </code>
                          <div className="api--key--actions">
                            <button
                              onClick={() => setShowApiKey({ ...showApiKey, [key.id]: !showApiKey[key.id] })}
                              className="btn--icon"
                              title={showApiKey[key.id] ? 'Hide' : 'Show'}
                            >
                              {showApiKey[key.id] ? <FaEyeSlash /> : <FaEye />}
                            </button>
                            <button
                              onClick={() => copyToClipboard(key.key)}
                              className="btn--icon"
                              title="Copy"
                            >
                              <FaCopy />
                            </button>
                          </div>
                        </div>
                        <div className="api--key--meta">
                          <span>Created: {key.createdAt ? new Date(key.createdAt).toLocaleDateString() : 'N/A'}</span>
                          {key.lastUsed && (
                            <span>Last used: {new Date(key.lastUsed).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => openDeleteModal(key.id, key.name || 'Unnamed Key')}
                        className="btn--delete"
                      >
                        Delete
                      </button>
                    </div>
                  ))
                )}
              </div>
            </section>
          </>
        )}

        {/* Webhooks Tab */}
        {activeTab === 'webhooks' && (
          <>
            <div className="webhooks--header">
              <div className="webhooks--title--section">
                <FaPlug className="icon" />
                <div>
                  <h2>Webhooks</h2>
                  <p>Receive real-time notifications about events in your Astar Logistics account. Webhooks allow your application to get data instantly when something happens.</p>
                  {/* Debug info - remove after fixing */}
                  <small style={{ color: 'yellow', display: 'block', marginTop: '0.5em' }}>
                    Debug: Webhooks count = {webhooks?.length || 0} | Is Array: {Array.isArray(webhooks) ? 'Yes' : 'No'}
                  </small>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowWebhookForm(true);
                  setWebhookFormData({ name: '', url: '', secret: '', events: [] });
                  setSelectedWebhook(null);
                }}
                className="btn--add--webhook"
              >
                <span>+</span> Add Webhook
              </button>
            </div>

            <div className="webhooks--container">
              {/* Left Side - Webhook List */}
              <div className="webhooks--sidebar">
                <div className="sidebar--header">
                  <h3>Your Webhooks</h3>
                </div>

                {!webhooks || webhooks.length === 0 ? (
                  <div className="empty--state--sidebar">
                    <FaPlug />
                    <p>No webhooks yet</p>
                    <p style={{fontSize: '0.8em', color: 'red'}}>
                      Webhooks: {JSON.stringify(webhooks)}
                    </p>
                  </div>
                ) : (
                  <div className="webhook--list--sidebar">
                    <p style={{color: 'lime', marginBottom: '1em'}}>
                      Found {webhooks.length} webhook(s)
                    </p>
                    {webhooks.map((webhook, index) => (
                      <div
                        key={webhook?.id || index}
                        className={`webhook--card ${selectedWebhook?.id === webhook?.id ? 'active' : ''}`}
                        onClick={() => viewWebhookDetails(webhook)}
                      >
                        <div className="webhook--card--icon">
                          <FaPlug />
                        </div>
                        <div className="webhook--card--content">
                          <div className="webhook--card--header">
                            <strong>{webhook?.name || 'Unnamed'}</strong>
                            <span className={`status--badge ${webhook?.active ? 'active' : 'inactive'}`}>
                              {webhook?.active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <div className="webhook--card--url">{webhook?.url || 'No URL'}</div>
                          <div className="webhook--card--meta">
                            {webhook?.events?.length || 0} Events
                          </div>
                        </div>
                        <button className="webhook--card--arrow">
                          <span>›</span>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <button className="view--logs--btn">
                  <FaExternalLinkAlt /> View Webhook Logs
                </button>
              </div>

              {/* Right Side - Webhook Details or Form */}
              <div className="webhooks--main">
                {!selectedWebhook && !showWebhookForm ? (
                  <div className="empty--state--main">
                    <FaPlug className="empty--icon" />
                    <h3>No webhook selected</h3>
                    <p>Select a webhook from the list or create a new one to get started</p>
                  </div>
                ) : showWebhookForm ? (
                  <div className="webhook--form--wrapper">
                    <div className="webhook--form--header">
                      <h3>{selectedWebhook ? 'Edit Webhook' : 'Add New Webhook'}</h3>
                      <button
                        onClick={() => {
                          setShowWebhookForm(false);
                          setSelectedWebhook(null);
                          setWebhookFormData({ name: '', url: '', secret: '', events: [] });
                        }}
                        className="btn--icon--close"
                      >
                        <FaTimes />
                      </button>
                    </div>

                    <form onSubmit={handleWebhookSubmit} className="webhook--form">
                      <div className="form--group">
                        <label>Webhook Name *</label>
                        <input
                          type="text"
                          value={webhookFormData.name}
                          onChange={(e) => setWebhookFormData({ ...webhookFormData, name: e.target.value })}
                          placeholder="e.g., Production Webhook, Staging Webhook"
                          required
                        />
                      </div>

                      <div className="form--group">
                        <label>Endpoint URL *</label>
                        <input
                          type="url"
                          value={webhookFormData.url}
                          onChange={(e) => setWebhookFormData({ ...webhookFormData, url: e.target.value })}
                          placeholder="https://api.yourapp.com/webhooks/pickup-logistics"
                          required
                        />
                      </div>

                      <div className="form--group">
                        <label>Secret (Optional)</label>
                        <input
                          type="text"
                          value={webhookFormData.secret}
                          onChange={(e) => setWebhookFormData({ ...webhookFormData, secret: e.target.value })}
                          placeholder="••••••••••••••••••••"
                        />
                        <small>Used to sign webhook payloads for verification</small>
                      </div>

                      <div className="form--group">
                        <label>Events Subscribed *</label>
                        <div className="events--grid">
                          {availableEvents.map(event => (
                            <div
                              key={event.id}
                              className={`event--checkbox--item ${webhookFormData.events.includes(event.id) ? 'checked' : ''}`}
                              onClick={() => toggleEvent(event.id)}
                            >
                              <div className="checkbox--icon">
                                {webhookFormData.events.includes(event.id) && <FaCheck />}
                              </div>
                              <div className="event--label">{event.label}</div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="form--actions">
                        <button
                          type="button"
                          onClick={() => {
                            setShowWebhookForm(false);
                            setSelectedWebhook(null);
                            setWebhookFormData({ name: '', url: '', secret: '', events: [] });
                          }}
                          className="btn btn--secondary"
                        >
                          Cancel
                        </button>
                        <button type="submit" className="btn btn--primary" disabled={webhookLoading}>
                          {webhookLoading ? 'Saving...' : (selectedWebhook ? 'Update Webhook' : 'Create Webhook')}
                        </button>
                      </div>
                    </form>
                  </div>
                ) : (
                  /* Webhook Details */
                  <div className="webhook--details--wrapper">
                    <div className="webhook--details--header">
                      <div>
                        <h3>{selectedWebhook.name}</h3>
                        <span className={`status--badge ${selectedWebhook.active ? 'active' : 'inactive'}`}>
                          {selectedWebhook.active ? 'Active' : 'Inactive'}
                        </span>
                        {selectedWebhook.lastDelivery && (
                          <span className="last--delivery">Last delivery: 2 minutes ago</span>
                        )}
                      </div>
                      <div className="header--actions">
                        <button
                          onClick={() => handleEditWebhook(selectedWebhook)}
                          className="btn--icon--action"
                          title="Edit"
                        >
                          <FaEdit /> Edit
                        </button>
                        <button
                          onClick={() => handleDeleteWebhook(selectedWebhook.id)}
                          className="btn--icon--action btn--danger"
                          title="Delete"
                        >
                          <FaTrash /> Delete
                        </button>
                      </div>
                    </div>

                    <div className="webhook--details--body">
                      <div className="detail--row">
                        <label>Endpoint URL</label>
                        <div className="detail--value--with--actions">
                          <code>{selectedWebhook.url}</code>
                          <div className="value--actions">
                            <button onClick={() => copyToClipboard(selectedWebhook.url)} className="btn--icon--small">
                              <FaCopy />
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="detail--row">
                        <label>Secret</label>
                        <div className="detail--value--with--actions">
                          <code>{selectedWebhook.secret ? '••••••••••••••••••••••••••••••••' : 'Not set'}</code>
                          <div className="value--actions">
                            <button className="btn--icon--small" title="Show/Hide">
                              <FaEye />
                            </button>
                            {selectedWebhook.secret && (
                              <button onClick={() => copyToClipboard(selectedWebhook.secret)} className="btn--icon--small">
                                <FaCopy />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="detail--row">
                        <label>Status</label>
                        <div className="detail--value">
                          {selectedWebhook.active ? (
                            <span className="status--text active">● Active</span>
                          ) : (
                            <span className="status--text inactive">● Inactive</span>
                          )}
                        </div>
                      </div>

                      <div className="detail--row">
                        <label>Created At</label>
                        <div className="detail--value">
                          {selectedWebhook.createdAt ? new Date(selectedWebhook.createdAt).toLocaleString() : 'N/A'}
                        </div>
                      </div>

                      <div className="detail--row">
                        <label>Last Updated</label>
                        <div className="detail--value">
                          {selectedWebhook.updatedAt ? new Date(selectedWebhook.updatedAt).toLocaleString() : 'N/A'}
                        </div>
                      </div>

                      <div className="detail--row">
                        <label>Events Subscribed</label>
                        <div className="detail--value">
                          <button className="manage--events--btn">Manage Events</button>
                        </div>
                      </div>

                      <div className="events--subscribed--grid">
                        {selectedWebhook.events?.map(eventId => {
                          const event = availableEvents.find(e => e.id === eventId);
                          return event ? (
                            <div key={eventId} className="event--badge--item">
                              <FaCheck /> {event.label}
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>

                    {/* Info Box */}
                    <div className="webhook--info--box">
                      <div className="info--icon">ℹ️</div>
                      <div>
                        <strong>How Webhooks Work</strong>
                        <p>
                          When an event occurs in your account, we'll send a POST request to your webhook URL with a JSON payload.
                          Learn more in our <a href="https://astar.ng/company/dashboard/webhook-docs" target="_blank" rel="noopener noreferrer">Webhook Documentation</a> <FaExternalLinkAlt style={{ fontSize: '0.8em' }} />
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        showModal={deleteModal.show}
        onClose={closeDeleteModal}
        modalTitle="Delete API Key"
      >
        <div style={{ padding: '1.5em 0' }}>
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '1em', lineHeight: '1.6' }}>
            Are you sure you want to delete the API key <strong style={{ color: '#FE7D11' }}>"{deleteModal.keyName}"</strong>?
          </p>
          <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>
            This action cannot be undone. Any applications using this key will lose access immediately.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '1em', justifyContent: 'flex-end', marginTop: '1.5em' }}>
          <Button
            onClick={closeDeleteModal}
            style={{
              padding: '0.75em 1.5em',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              color: '#fff',
              cursor: 'pointer'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteApiKey(deleteModal.keyId)}
            style={{
              padding: '0.75em 1.5em',
              background: 'rgba(244, 67, 54, 0.2)',
              border: '1px solid rgba(244, 67, 54, 0.4)',
              borderRadius: '8px',
              color: '#E57373',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Delete Key
          </Button>
        </div>
      </Modal>
    </Container>
  );
};

export default Settings;
